import { clsx } from 'clsx'
import type { ReactNode, VFC } from 'react'

import CdnImage from 'views/components/atoms/CdnImage'
import Image from 'views/components/atoms/Image'
import {
  IMAGE_MAX_HEIGHT,
  IMAGE_MAX_WIDTH,
  IMAGE_MIN_WIDTH,
  IMAGE_SIZES,
} from 'views/components/organisms/FilteredProductList/consts'

import { CLASS_SELECTABLE } from './consts'

type ImageProps = {
  discount: number
  imageUrl: string
  multiplierRP: number
  name: string
  prescriptionMedicine: boolean
  prescriptionType: string
  prices: []
  productTemplate: string
  redPoints: number
  redPointsIsActive: boolean
  redPointsMultiplierIsValid: boolean
  srcHeight: number
  srcWidth: number
  thumbnail: boolean
}

type SearchProductListItemComponentProps = {
  addToCartButtonComponent: ReactNode
  className: string
  currentOfferId: string
  descriptionShort: string
  discountBadge: ReactNode
  highlighted: boolean
  highlightedProductLabel: ReactNode
  idSwitcher: ReactNode
  imageProps: ImageProps
  isAvailable: boolean
  isCdnUrl: boolean
  isItemWithMultipleVariantAttributes: boolean
  isRebrandingEnabled: boolean
  isRxCh: boolean
  lazyLoadImage: boolean
  listPriceWithDiscount: ReactNode
  manufacturer: string
  odsPartnersComponent: ReactNode
  pharmaForm: string
  productStatus: ReactNode
  productUrl: string
  productUrlWithImageEvent: string
  pzn: string
  quantityUnit: string
  redpointsMultiplier: number
  renderProductItemLinkWithIcon: ReactNode
  renderProductVariantsBox: ReactNode
  retailPriceComponent: ReactNode
  searchProductLabelsComponent: ReactNode
  searchProductListItemProps: Record<string, unknown>
  sellerBoxComponent: ReactNode
  starRatingComponent: ReactNode
  textInfoClassName: string
  titleClassName: string
  unitPricingComponent: ReactNode
  otherPackSizesText: ReactNode
}

const SearchProductListItemComponent: VFC<
  SearchProductListItemComponentProps
> = ({
  addToCartButtonComponent,
  className,
  currentOfferId,
  descriptionShort,
  discountBadge,
  productUrl,
  highlighted,
  highlightedProductLabel,
  idSwitcher,
  imageProps,
  isAvailable,
  isCdnUrl,
  isItemWithMultipleVariantAttributes,
  isRxCh,
  lazyLoadImage,
  listPriceWithDiscount,
  manufacturer,
  odsPartnersComponent,
  pharmaForm,
  productStatus,
  productUrlWithImageEvent,
  quantityUnit,
  renderProductItemLinkWithIcon,
  renderProductVariantsBox,
  retailPriceComponent,
  searchProductLabelsComponent,
  searchProductListItemProps,
  sellerBoxComponent,
  starRatingComponent,
  textInfoClassName,
  titleClassName,
  unitPricingComponent,
  otherPackSizesText,
}) => {
  const { imageUrl, name } = imageProps

  const buySectionClassName = clsx(
    'l-flex l-flex--end l-flex--wrap o-SearchProductListItem__content__buy-section',
    isRxCh ? 'l-flex--justify-content--end' : 'l-flex--distribute-space'
  )
  return (
    <li {...searchProductListItemProps} className={className}>
      {highlighted && (
        <div className="o-SearchProductListItem__highlighted-area u-border u-border-radius--top-left u-border-radius--top-right a-h3">
          {highlightedProductLabel}
        </div>
      )}
      {searchProductLabelsComponent}
      <div
        data-clientside-hook="productDetailButton"
        className="o-SearchProductListItem__link u-color--dark u-padding--sides u-padding--bottom l-flex l-flex--wrap l-flex--stretch-children"
      >
        <a
          className="o-SearchProductListItem__image"
          href={productUrlWithImageEvent}
          draggable={false}
        >
          {isCdnUrl ? (
            <CdnImage
              alt={name}
              imgClassName="a-fullwidth-image"
              fileName={imageUrl}
              lazyLoad={lazyLoadImage}
              maxHeight={IMAGE_MAX_HEIGHT}
              maxWidth={IMAGE_MAX_WIDTH}
              minWidth={IMAGE_MIN_WIDTH}
              preconnect={!lazyLoadImage}
              sizes={IMAGE_SIZES}
            />
          ) : (
            <Image
              alt={name}
              className="a-fullwidth-image"
              lazyLoad={lazyLoadImage}
              preconnect={!lazyLoadImage}
              src={imageUrl}
            />
          )}
        </a>
        <div className="o-SearchProductListItem__content l-flex l-flex--stretch-children l-flex__primary l-flex--wrap@small-tiny">
          <div className="l-col--12-of-12 l-col--7-of-12@medium u-padding--sides o-SearchProductListItem__content__header">
            <div className="u-margin--bottom o-SearchProductListItem__content__header__top">
              <a
                href={productUrl}
                className={clsx(
                  'o-SearchProductListItem__title-link',
                  titleClassName
                )}
                data-qa-id={
                  Boolean(currentOfferId) && `offers-link-${currentOfferId}`
                }
                draggable={false}
              >
                {name}
              </a>
              {starRatingComponent}
            </div>
            <div className={CLASS_SELECTABLE}>
              <ul className="o-SearchProductListItem__info-list u-margin--bottom u-margin-small--bottom@small-tiny">
                {!isItemWithMultipleVariantAttributes && (
                  <li className="o-SearchProductListItem__info-list__item">
                    <span
                      className="a-h4-tiny u-font-weight--bold o-SearchProductListItem__info__items"
                      suppressHydrationWarning
                    >
                      {quantityUnit}
                      {pharmaForm ? ` | ${pharmaForm}` : ''}
                    </span>
                  </li>
                )}
                {idSwitcher}
                {manufacturer && (
                  <li className="o-SearchProductListItem__info-list__item">
                    <span className="a-h4-tiny o-SearchProductListItem__info__items">
                      {manufacturer}
                    </span>
                  </li>
                )}
              </ul>
              {productStatus}
            </div>
          </div>
          <div className={textInfoClassName}>
            {descriptionShort && (
              <div className="a-link--neutral u-hidden@small-tiny">
                <p className="a-h4-tiny o-SearchProductListItem__content__body__text">
                  {descriptionShort}
                </p>
              </div>
            )}
            {odsPartnersComponent}
            <div className={buySectionClassName}>
              {isAvailable && (
                <div className="o-SearchProductListItem__content__buy-section-wrapper">
                  {renderProductItemLinkWithIcon}
                  <div className="l-flex o-SearchProductListItem__prices__retail-price-wrapper">
                    {discountBadge}
                    {retailPriceComponent}
                  </div>
                  {unitPricingComponent}
                  {listPriceWithDiscount}
                </div>
              )}

              <div className="l-flex@medium l-flex--justify-content--end o-SearchProductListItem__content__buy-section__cart-section">
                {addToCartButtonComponent}
              </div>
              {sellerBoxComponent}
              {otherPackSizesText}
            </div>
          </div>
        </div>
      </div>
      {renderProductVariantsBox}
    </li>
  )
}

export default SearchProductListItemComponent
